import {
	Box,
	Stack,
	Button,
	Icons,
	Inline,
	Table,
	joinAttributes,
	Time,
	Tooltip,
	SelectInput,
	Text,
	Money,
	Tabs,
	TabContent,
	TabsList,
	TabItem,
	RelativeTime,
	Stars,
	SwitchInput,
	Component,
} from "@sembark-travel/ui-base"
import { Link, useLocationQuery, ButtonLink } from "@sembark-travel/ui-router"
import {
	Search,
	TSearchParams,
	useSearch,
	areAdvancedFiltersAppliedDefault,
	ListView,
} from "@sembark-travel/ui-list"
import {
	dateToQuery,
	dateToUTCString,
	parseDateFromQuery,
	startOf,
	endOf,
	subtractUnit,
	formatDate,
	addUnit,
	utcTimestampToLocalDateTimeString,
	localOrUtcTimestampToLocalDate,
	getDiff,
	utcTimestampToLocalDate,
	isSameOrAfter,
	isSameOrBefore,
} from "@sembark-travel/datetime-utils"
import { IListResponse, XHRInstance } from "@sembark-travel/xhr"
import { useEffect, useMemo, useRef } from "react"
import { SelectTripDestination } from "../TripDestinations"
import { TTripDestination } from "../TripDestinations"
import { ITrip } from "../Trips"
import { SelectTripSources } from "../TripSources"
import { ITripSource } from "../TripSources"
import { IUser, SelectUsers } from "../Users"
import {
	DatePickerField,
	SelectField,
	useFieldValue,
	useForm,
} from "@sembark-travel/ui-form"
import { moneyParseByDecimal } from "@sembark-travel/money"
import { isNumeric, withOrdinalSuffix } from "@sembark-travel/number-utils"
import pluralize from "pluralize"
import {
	HotelBookingCurrentStage,
	IBooking,
	UpdateBookingTagInDialog,
} from "../HotelBookings"
import { SelectHotelBookingStageTags } from "../HotelBookings/BookingStatus"
import { IHotel, SelectHotels } from "../Hotels"
import { IHotelGroup, SelectHotelGroup } from "../HotelGroups"
import { ILocation } from "../Locations"
import {
	GenerateVoucher,
	HotelBookingItemInDialog,
} from "../HotelBookings/Item"
import { GeneratedVoucherAsset, MarkVoucherAsSent } from "../HotelVouchers"
import { PhoneNumber } from "../Contacts"
import { IMealPlan } from "../MealPlans"
import { DownloadManager } from "../DownloadManager"
import { SelectHotelBookingStages } from "../HotelBookingStages"
import { SelectHotelBookingPaymentStatuses } from "../HotelBookings/List"
import { generatePath } from "../router-utils"

const defaultQuickStagesFilter = [
	{ id: "-changed", name: "Changed" },
	{ id: "-dropped", name: "Dropped" },
	// { id: "-trip_dropped", name: "Trip Dropped" },
]

// Utility functions for checking advanced filters
function hasAdvancedBookingStages(
	bookingStages?: Array<{ id: string; name: string }>
) {
	return (
		bookingStages &&
		bookingStages.length > 0 &&
		!bookingStages.every((b) => b.id.startsWith("-"))
	)
}

function hasAdvancedPaymentStatuses(
	paymentStatuses?: Array<{ id: string; name: string }>
) {
	return paymentStatuses && paymentStatuses.length > 0
}

function XHR(xhr: XHRInstance) {
	return {
		async getBookings(
			params: unknown
		): Promise<IListResponse<IBooking & { trip: ITrip }>> {
			return xhr.get("/hotel-bookings", { params }).then((resp) => resp.data)
		},
	}
}

type TFilters = TSearchParams & {
	overall_checkin_after?: Date
	overall_checkin_before?: Date
	overall_checkout_after?: Date
	overall_checkout_before?: Date
	display_after: Date
	display_before: Date
	booking_tags?: Array<{ id: number; name: string }>
	booking_stages?: Array<{ id: string; name: string }>
	quick_booking_stages?: Array<{ id: string; name: string }>
	payment_statuses?: Array<{ id: string; name: string }>
	trip_destinations?: Array<TTripDestination>
	trip_sources?: Array<ITripSource>
	trip_owners?: Array<IUser>
	hotels?: Array<IHotel>
	hotel_groups?: Array<IHotelGroup>
	locations?: Array<ILocation>
	report_type: "overall_checkin" | "overall_checkout"
	report_stage: "all" | "completed" | "upcoming"
	interval_type: "month" | "week" | "day"
}

type TFiltersInQuery = TSearchParams & {
	overall_checkin_after?: string
	overall_checkin_before?: string
	overall_checkout_after?: string
	overall_checkout_before?: string
	display_after: string
	display_before: string
	booking_tags?: Array<string>
	bs?: Array<string>
	ps?: Array<string>
	trip_destinations?: Array<string>
	trip_sources?: Array<string>
	trip_owners?: Array<string>
	hotels?: Array<string>
	hotel_groups?: Array<string>
	locations?: string[]
	report_type: "overall_checkin" | "overall_checkout"
	report_stage: "all" | "completed" | "upcoming"
	interval_type: "month" | "week" | "day"
}

function filtersToQuery(filters: TFilters): TFiltersInQuery {
	const {
		booking_tags,
		booking_stages,
		payment_statuses,
		trip_destinations,
		trip_sources,
		trip_owners,
		q,
		page,
		report_type = "overall_checkin",
		interval_type = "week",
		report_stage = "upcoming",
		hotel_groups,
		hotels,
		locations,
		overall_checkin_after,
		overall_checkout_after,
	} = filters

	const {
		display_after,
		display_before,
		check_in_after,
		check_in_before,
		check_out_after,
		check_out_before,
	} = getHotelCheckInOutBeforeAfterDates({
		report_type,
		interval_type,
		report_stage,
		date:
			report_type === "overall_checkin"
				? overall_checkin_after
				: overall_checkout_after,
	})

	const query: TFiltersInQuery = {
		interval_type: interval_type,
		report_stage: report_stage,
		report_type: report_type,
		display_after: dateToQuery(display_after),
		display_before: dateToQuery(display_before),
	}

	// Only include check-in parameters for check-in reports
	if (report_type === "overall_checkin") {
		query.overall_checkin_after = check_in_after
			? dateToQuery(check_in_after)
			: undefined
		query.overall_checkin_before = check_in_before
			? dateToQuery(check_in_before)
			: undefined
	}
	// Only include check-out parameters for check-out reports
	else {
		query.overall_checkout_after = check_out_after
			? dateToQuery(check_out_after)
			: undefined
		query.overall_checkout_before = check_out_before
			? dateToQuery(check_out_before)
			: undefined
	}

	if (q) {
		query.q = q
	}

	if (page) {
		query.page = page
	}

	if (hotels && hotels.length)
		query.hotels = hotels.map((t) => `${t.id}_${t.name}`)

	if (hotel_groups && hotel_groups.length)
		query.hotel_groups = hotel_groups.map((t) => `${t.id}_${t.name}`)

	if (trip_destinations && trip_destinations.length)
		query.trip_destinations = trip_destinations.map((t) => `${t.id}_${t.name}`)

	if (booking_tags && booking_tags.length)
		query.booking_tags = booking_tags.map((t) => `${t.id}_${t.name}`)

	// Only include advanced booking_stages in the query
	if (
		booking_stages &&
		booking_stages.length > 0 &&
		!booking_stages.every((b) => b.id.startsWith("-"))
	) {
		query.bs = booking_stages.map((b) => `${b.id}#${b.name}`)
	}

	if (payment_statuses && payment_statuses.length)
		query.ps = payment_statuses.map((p) => `${p.id}#${p.name}`)

	if (trip_sources && trip_sources.length)
		query.trip_sources = trip_sources.map((t) => `${t.id}_${t.name}`)
	if (trip_owners && trip_owners.length)
		query.trip_owners = trip_owners.map((t) => `${t.id}_${t.name}`)

	if (locations && locations.length)
		query.locations = locations.map((t) => `${t.id}_${t.name}`)

	return query
}

function queryToFilters(query: TFiltersInQuery): TFilters {
	const {
		booking_tags,
		bs,
		ps,
		trip_destinations,
		trip_sources,
		trip_owners,
		report_type = "overall_checkin",
		interval_type = "week",
		report_stage = "upcoming",
		q,
		page,
		hotel_groups,
		hotels,
		locations,
		overall_checkin_after,
		overall_checkout_after,
	} = query

	const {
		display_after,
		display_before,
		check_in_after,
		check_in_before,
		check_out_after,
		check_out_before,
	} = getHotelCheckInOutBeforeAfterDates({
		report_type,
		interval_type,
		report_stage,
		date:
			report_type === "overall_checkin"
				? overall_checkin_after
					? parseDateFromQuery(overall_checkin_after)
					: undefined
				: overall_checkout_after
					? parseDateFromQuery(overall_checkout_after)
					: undefined,
	})

	const filters: TFilters = {
		overall_checkin_after: check_in_after,
		overall_checkin_before: check_in_before,
		overall_checkout_after: check_out_after,
		overall_checkout_before: check_out_before,
		interval_type: interval_type,
		report_stage: report_stage,
		report_type: report_type,
		display_after: display_after,
		display_before: display_before,
	}

	if (booking_tags && booking_tags.length) {
		filters.booking_tags = booking_tags.map((t) => {
			const slices = t.split("_")
			return { id: parseInt(slices[0]), name: slices.slice(1).join("_") }
		}) as unknown as Array<{ id: number; name: string }>
	}

	if (bs && bs.length) {
		filters.booking_stages = bs.map((b) => {
			const [id, ...name] = b.split("#")
			return {
				id,
				name: name.join("#"),
			}
		})
	}

	if (ps && ps.length) {
		filters.payment_statuses = ps.map((p) => {
			const [id, ...name] = p.split("#")
			return { id, name: name.join("#") }
		})
	}

	if (trip_destinations && trip_destinations.length) {
		filters.trip_destinations = trip_destinations.map((d) => {
			const slices = d.split("_")
			return { id: parseInt(slices[0]), name: slices.slice(1).join("_") }
		}) as unknown as Array<TTripDestination>
	}

	if (trip_sources && trip_sources.length) {
		filters.trip_sources = trip_sources.map((d) => {
			const slices = d.split("_")
			return { id: parseInt(slices[0]), name: slices.slice(1).join("_") }
		}) as unknown as Array<ITripSource>
	}

	if (trip_owners && trip_owners.length) {
		filters.trip_owners = trip_owners.map((d) => {
			const slices = d.split("_")
			return { id: parseInt(slices[0]), name: slices.slice(1).join("_") }
		}) as unknown as Array<IUser>
	}

	if (hotels && hotels.length) {
		filters.hotels = hotels.map((d) => {
			const slices = d.split("_")
			return { id: parseInt(slices[0]), name: slices.slice(1).join("_") }
		}) as unknown as Array<IHotel>
	}

	if (hotel_groups && hotel_groups.length)
		filters.hotel_groups = hotel_groups.map((t) => {
			const slices = t.split("_")
			return { id: parseInt(slices[0]), name: slices.slice(1).join("_") }
		}) as unknown as Array<IHotelGroup>

	if (locations && locations.length)
		filters.locations = locations.map((t) => {
			const slices = t.split("_")
			return { id: parseInt(slices[0]), name: slices.slice(1).join("_") }
		}) as unknown as Array<ILocation>

	if (q) {
		filters.q = q
	}

	if (page) {
		filters.page = page
	}

	// If there are no advanced booking_stages and no quick_booking_stages, set quick_booking_stages to default
	const hasAdvancedStages = hasAdvancedBookingStages(filters.booking_stages)
	const hasQuickStages =
		filters.quick_booking_stages && filters.quick_booking_stages.length > 0
	if (!hasAdvancedStages && !hasQuickStages) {
		filters.quick_booking_stages = defaultQuickStagesFilter
	}

	return filters
}

function filtersToRequestParams(params: TFilters) {
	let stagesToSend: Array<{ id: string; name: string }> = []
	const hasAdvancedBookingStagesApplied = hasAdvancedBookingStages(
		params.booking_stages
	)
	const hasAdvancedPaymentStatusesApplied = hasAdvancedPaymentStatuses(
		params.payment_statuses
	)

	if (hasAdvancedBookingStagesApplied || hasAdvancedPaymentStatusesApplied) {
		// Only use advanced booking_stages, do NOT include quick filters
		stagesToSend = params.booking_stages || []
		// If advanced booking_stages contains "dropped", add "trip_dropped"
		const hasDropped = stagesToSend.some((b) => b.id === "dropped")
		if (hasDropped) {
			stagesToSend = [
				...stagesToSend,
				{ id: "trip_dropped", name: "Trip Dropped" },
			]
		}
	} else {
		stagesToSend = params.quick_booking_stages || []
		const hasDropped = stagesToSend.some((b) => b.id === "-dropped")
		//send "-trip_dropped" to "booking_stages" if quick filter is applied and contains "-dropped"
		if (hasDropped) {
			stagesToSend = [
				...stagesToSend,
				{ id: "-trip_dropped", name: "Trip Dropped" },
			]
		}
	}

	return {
		q: params.q || null,
		page: params.page || null,
		overall_checkin_after: params.overall_checkin_after
			? dateToUTCString(params.overall_checkin_after)
			: null,
		overall_checkin_after_local: params.overall_checkin_after
			? formatDate(params.overall_checkin_after, "YYYY-MM-DD")
			: null,
		overall_checkin_before: params.overall_checkin_before
			? dateToUTCString(params.overall_checkin_before)
			: null,
		overall_checkin_before_local: params.overall_checkin_before
			? formatDate(params.overall_checkin_before, "YYYY-MM-DD")
			: null,
		overall_checkout_after: params.overall_checkout_after
			? dateToUTCString(params.overall_checkout_after)
			: null,
		overall_checkout_after_local: params.overall_checkout_after
			? formatDate(params.overall_checkout_after, "YYYY-MM-DD")
			: null,
		overall_checkout_before: params.overall_checkout_before
			? dateToUTCString(params.overall_checkout_before)
			: null,
		overall_checkout_before_local: params.overall_checkout_before
			? formatDate(params.overall_checkout_before, "YYYY-MM-DD")
			: null,
		booking_tags: (params.booking_tags || []).map((t) => t.name),
		booking_stages: (stagesToSend || []).map((b) => b.id),
		payment_statuses: (params.payment_statuses || []).map((p) => p.id),
		trip_destinations: (params.trip_destinations || []).map((d) => d.id),
		trip_sources: (params.trip_sources || []).map((d) => d.id),
		trip_owners: (params.trip_owners || []).map((d) => d.id),
		hotels: (params.hotels || []).map((t) => t.id),
		hotel_groups: (params.hotel_groups || []).map((t) => t.id),
		sort:
			params.report_stage === "completed"
				? `-${params.report_type}`
				: params.report_type,
	}
}

function getHotelCheckInOutBeforeAfterDates({
	report_type,
	interval_type,
	report_stage,
	date,
}: {
	report_type: TFilters["report_type"]
	interval_type: TFilters["interval_type"]
	report_stage: TFilters["report_stage"]
	date?: Date
}) {
	const today = startOf(new Date(), "day")
	const givenDate = date ? startOf(date, "day") : today
	const start = startOf(givenDate, interval_type)
	const end = endOf(givenDate, interval_type)

	let queryCheckInAfter: Date | undefined = undefined
	let queryCheckInBefore: Date | undefined = undefined
	let queryCheckOutAfter: Date | undefined = undefined
	let queryCheckOutBefore: Date | undefined = undefined

	let displayAfter: Date = start
	let displayBefore: Date = end

	if (start > today) {
		// Future window
		if (report_stage === "completed") {
			// Sending this query for intentioanlly need empty results for completed in future window

			if (report_type === "overall_checkin") {
				queryCheckInAfter = end
				queryCheckInBefore = end
			} else {
				queryCheckOutAfter = end
				queryCheckOutBefore = end
			}
		} else {
			// For "upcoming" and "all", showing the entire interval
			if (report_type === "overall_checkin") {
				queryCheckInAfter = start
				queryCheckInBefore = end
			} else {
				queryCheckOutAfter = start
				queryCheckOutBefore = end
			}
		}
	} else if (end < today) {
		// Past window
		if (report_stage === "upcoming") {
			// Sending this query for intentioanlly need empty results for upcoming in past
			if (report_type === "overall_checkin") {
				queryCheckInAfter = end
				queryCheckInBefore = end
			} else {
				queryCheckOutAfter = end
				queryCheckOutBefore = end
			}
		} else {
			// For "completed" and "all", show the entire interval
			if (report_type === "overall_checkin") {
				queryCheckInAfter = start
				queryCheckInBefore = end
			} else {
				queryCheckOutAfter = start
				queryCheckOutBefore = end
			}
		}
	} else {
		// Current window (overlaps with today)
		if (report_type === "overall_checkin") {
			if (report_stage === "upcoming") {
				queryCheckInAfter = today
				queryCheckInBefore = end
				displayAfter = today
				displayBefore = end
			} else if (report_stage === "completed") {
				queryCheckInAfter = start
				queryCheckInBefore = endOf(subtractUnit(today, 1, "day"), "day") // Exclude today for completed and sending the end of the previous day for completed
				displayAfter = start
				displayBefore = endOf(subtractUnit(today, 1, "day"), "day") // Exclude today from display for completed and sending the end of the previous day for completed
			} else {
				// "all"
				queryCheckInAfter = start
				queryCheckInBefore = end
			}
		} else if (report_type === "overall_checkout") {
			if (report_stage === "upcoming") {
				queryCheckOutAfter = today
				queryCheckOutBefore = end
				displayAfter = today
				displayBefore = end
			} else if (report_stage === "completed") {
				queryCheckOutAfter = start
				queryCheckOutBefore = endOf(subtractUnit(today, 1, "day"), "day") // Exclude today for completed and sending the end of the previous day for completed

				displayAfter = start
				displayBefore = endOf(subtractUnit(today, 1, "day"), "day") // Exclude today from display for completed and sending the end of the previous day for completed
			} else {
				// "all"
				queryCheckOutAfter = start
				queryCheckOutBefore = end
			}
		}
	}

	return {
		display_after: displayAfter,
		display_before: displayBefore,
		check_in_after: queryCheckInAfter,
		check_in_before: queryCheckInBefore,
		check_out_after: queryCheckOutAfter,
		check_out_before: queryCheckOutBefore,
	}
}

function useQuickFilterSync(
	state: { includeChanged: boolean; includeDropped: boolean },
	searchParams: TFilters,
	setSearchParams: (params: Partial<TFilters>) => void
) {
	useEffect(() => {
		let quickStages: Array<{ id: string; name: string }> = []
		if (state.includeChanged && state.includeDropped) quickStages = []
		else if (state.includeChanged && !state.includeDropped)
			quickStages = [{ id: "-dropped", name: "Dropped" }]
		else if (!state.includeChanged && state.includeDropped)
			quickStages = [{ id: "-changed", name: "Changed" }]
		else quickStages = defaultQuickStagesFilter
		if (
			JSON.stringify(searchParams.quick_booking_stages) !==
			JSON.stringify(quickStages)
		) {
			setSearchParams({ ...searchParams, quick_booking_stages: quickStages })
		}
		// eslint-disable-next-line
	}, [state.includeChanged, state.includeDropped, searchParams])
}

function QuickFilterBox({
	state,
	setState,
	searchParams,
	setSearchParams,
}: {
	state: { includeChanged: boolean; includeDropped: boolean }
	setState: (s: { includeChanged: boolean; includeDropped: boolean }) => void
	searchParams: TFilters
	setSearchParams: (params: Partial<TFilters>) => void
}) {
	useQuickFilterSync(state, searchParams, setSearchParams)
	return (
		<Box
			display="flex"
			alignItems="center"
			justifyContent={"end"}
			marginTop={{
				xs: "1",
				lg: "0",
			}}
		>
			<Inline
				backgroundColor="default"
				borderRadius="md"
				paddingX="2"
				paddingY="2"
				boxShadow="sm"
				gap="2"
			>
				<Box marginRight={"3"}>
					<Text fontWeight="semibold" color="muted" fontSize="sm">
						Quick Filters:
					</Text>
				</Box>
				<Inline gap="3">
					<Inline alignItems="center" gap="1">
						<SwitchInput
							checked={state.includeChanged}
							onChange={(checked: 0 | 1) =>
								setState({
									includeChanged: checked === 1,
									includeDropped: state.includeDropped,
								})
							}
							id="include_changed_switch"
						/>
						<label htmlFor="include_changed_switch">
							<Text fontSize="sm">Include Changed</Text>
						</label>
					</Inline>
					<Inline alignItems="center" gap="1">
						<SwitchInput
							checked={state.includeDropped}
							onChange={(checked: 0 | 1) =>
								setState({
									includeChanged: state.includeChanged,
									includeDropped: checked === 1,
								})
							}
							id="include_dropped_switch"
						/>
						<label htmlFor="include_dropped_switch">
							<Text fontSize="sm">Include Dropped</Text>
						</label>
					</Inline>
				</Inline>
			</Inline>
		</Box>
	)
}

export function HotelCheckInOutReportsList() {
	const [query, setQuery] = useLocationQuery({
		toQuery: filtersToQuery,
		fromQuery: queryToFilters,
	})
	const [params, setParams] = useSearch<TFilters>(query)

	// Advanced filter detection for booking_stages and payment_statuses to avoid showing quick filters in these cases
	const hasAdvancedFilters =
		hasAdvancedBookingStages(params.booking_stages) ||
		hasAdvancedPaymentStatuses(params.payment_statuses)

	const requestParams = useMemo(() => {
		return filtersToRequestParams(params)
	}, [params])

	useEffect(() => {
		setQuery(params)
	}, [params, setQuery])

	return (
		<Box>
			<Search
				title={
					<Box
						fontSize={{
							xs: "md",
							xl: "lg",
						}}
					>
						{params.report_type === "overall_checkin" ? (
							<Inline gap="3" alignItems={"center"}>
								Hotel Check-Ins
								<Text
									as="span"
									fontSize={{
										xs: "sm",
										lg: "md",
									}}
								>
									(
									{formatDate(
										params.display_after as Date | string,
										"ddd D MMM"
									)}{" "}
									-{" "}
									{formatDate(
										params.display_before as Date | string,
										"ddd D MMM"
									)}
									)
								</Text>
							</Inline>
						) : (
							<Inline gap="3" alignItems={"center"}>
								Hotel Check-Outs
								<Text
									as="span"
									fontSize={{
										xs: "sm",
										lg: "md",
									}}
								>
									(
									{formatDate(
										params.display_after as Date | string,
										"ddd D MMM"
									)}{" "}
									-{" "}
									{formatDate(
										params.display_before as Date | string,
										"ddd D MMM"
									)}
									)
								</Text>
							</Inline>
						)}
					</Box>
				}
				initialParams={params}
				onSearch={setParams}
				Filters={Filters}
				resetParams={(params) => ({
					q: "",
					page: 1,
					overall_checkin_after: params.overall_checkin_after,
					overall_checkin_before: params.overall_checkin_before,
					overall_checkout_after: params.overall_checkout_after,
					overall_checkout_before: params.overall_checkout_before,
					display_after: params.display_after,
					display_before: params.display_before,
					interval_type: params.interval_type,
					report_type: params.report_type,
					report_stage: params.report_stage,
				})}
				areAdvancedFiltersApplied={(params) => {
					const {
						overall_checkin_after,
						overall_checkin_before,
						overall_checkout_after,
						overall_checkout_before,
						interval_type,
						report_type,
						report_stage,
						display_after,
						display_before,
						quick_booking_stages,
						...others
					} = params

					return areAdvancedFiltersAppliedDefault(others)
				}}
				actions={({ setSearchParams, searchParams }) => (
					<Inline gap="2" flexWrap="wrap">
						{!isCurrentTimeWindow(
							searchParams.report_type === "overall_checkin"
								? (searchParams.overall_checkin_after as Date)
								: (searchParams.overall_checkout_after as Date),
							searchParams.interval_type
						) ? (
							<Button
								level="secondary"
								onClick={() => {
									const {
										display_after,
										display_before,
										check_in_after,
										check_in_before,
										check_out_after,
										check_out_before,
									} = getHotelCheckInOutBeforeAfterDates({
										report_type: searchParams.report_type,
										interval_type: searchParams.interval_type,
										report_stage: "upcoming",
									})

									setSearchParams({
										...searchParams,
										page: 1,
										overall_checkin_after: check_in_after,
										overall_checkin_before: check_in_before,
										overall_checkout_after: check_out_after,
										overall_checkout_before: check_out_before,
										display_after,
										display_before,
										report_stage: "upcoming",
									})
								}}
							>
								<Icons.Clock />
								Today
							</Button>
						) : null}
						<Inline alignItems="center">
							<Button
								onClick={() => {
									const newDate = subtractUnit(
										searchParams.report_type === "overall_checkin"
											? (searchParams.overall_checkin_after as Date | string)
											: (searchParams.overall_checkout_after as Date | string),
										1,
										searchParams.interval_type
									)
									const {
										display_after,
										display_before,
										check_in_after,
										check_in_before,
										check_out_after,
										check_out_before,
									} = getHotelCheckInOutBeforeAfterDates({
										report_type: searchParams.report_type,
										interval_type: searchParams.interval_type,
										report_stage: searchParams.report_stage,
										date: newDate,
									})

									setSearchParams({
										...searchParams,
										page: 1,
										overall_checkin_after: check_in_after,
										overall_checkin_before: check_in_before,
										overall_checkout_after: check_out_after,
										overall_checkout_before: check_out_before,
										display_after,
										display_before,
									})
								}}
							>
								<Icons.ChevronDown rotate="90" />
							</Button>
							<Inline paddingX="2">
								<SelectInput
									value={searchParams.interval_type}
									onChange={(e) => {
										const type = (e.currentTarget.value ||
											"week") as typeof searchParams.interval_type
										const {
											display_after,
											display_before,
											check_in_after,
											check_in_before,
											check_out_after,
											check_out_before,
										} = getHotelCheckInOutBeforeAfterDates({
											report_type: searchParams.report_type,
											interval_type: type,
											report_stage: searchParams.report_stage,
											date:
												searchParams.report_type === "overall_checkin"
													? searchParams.overall_checkin_after
													: searchParams.overall_checkout_after,
										})
										setSearchParams({
											...searchParams,
											page: 1,
											interval_type: type,
											overall_checkin_after: check_in_after,
											overall_checkin_before: check_in_before,
											overall_checkout_after: check_out_after,
											overall_checkout_before: check_out_before,
											display_after,
											display_before,
										})
									}}
								>
									<option value="month">Month</option>
									<option value="week">Week</option>
									<option value="day">Day</option>
								</SelectInput>
							</Inline>
							<Button
								onClick={() => {
									const newDate = addUnit(
										searchParams.report_type === "overall_checkin"
											? (searchParams.overall_checkin_after as Date | string)
											: (searchParams.overall_checkout_after as Date | string),
										1,
										searchParams.interval_type
									)
									const {
										display_after,
										display_before,
										check_in_after,
										check_in_before,
										check_out_after,
										check_out_before,
									} = getHotelCheckInOutBeforeAfterDates({
										report_type: searchParams.report_type,
										interval_type: searchParams.interval_type,
										report_stage: searchParams.report_stage,
										date: newDate,
									})

									setSearchParams({
										...searchParams,
										page: 1,
										overall_checkin_after: check_in_after,
										overall_checkin_before: check_in_before,
										overall_checkout_after: check_out_after,
										overall_checkout_before: check_out_before,
										display_after,
										display_before,
									})
								}}
							>
								<Icons.ChevronDown rotate="270" />
							</Button>
						</Inline>

						<ButtonLink to={generatePath("/hotel-bookings/calendar")}>
							<Icons.Calendar />
						</ButtonLink>
						<Button
							onClick={() => {
								const {
									display_after,
									display_before,
									check_in_after,
									check_in_before,
									check_out_after,
									check_out_before,
								} = getHotelCheckInOutBeforeAfterDates({
									report_type:
										searchParams.report_type === "overall_checkin"
											? "overall_checkout"
											: "overall_checkin",
									interval_type: searchParams.interval_type,
									report_stage: searchParams.report_stage,
									date:
										searchParams.report_type === "overall_checkin"
											? searchParams.overall_checkin_after
											: searchParams.overall_checkout_after,
								})
								setSearchParams({
									...searchParams,
									page: 1,
									report_type:
										searchParams.report_type === "overall_checkin"
											? "overall_checkout"
											: "overall_checkin",
									overall_checkin_after: check_in_after,
									overall_checkin_before: check_in_before,
									overall_checkout_after: check_out_after,
									overall_checkout_before: check_out_before,
									display_after,
									display_before,
								})
							}}
							status="primary"
						>
							{searchParams.report_type === "overall_checkin"
								? "Check-Outs"
								: "Check-Ins"}
						</Button>

						<DownloadManager
							mode="customColumnsWithDateRange"
							title={`Export Hotel ${params.report_type === "overall_checkin" ? "Check-Ins" : "Check-Outs"} Report`}
							reportType={params.report_type}
							downloadUrl="/hotel-bookings/checkin-checkout-export"
							startDate={
								params.report_type === "overall_checkin"
									? params.overall_checkin_after
									: params.overall_checkout_after
							}
							endDate={
								params.report_type === "overall_checkin"
									? params.overall_checkin_before
									: params.overall_checkout_before
							}
							getRequestParams={(after, before) => ({
								...requestParams,
								overall_checkin_after: params.overall_checkin_after
									? dateToUTCString(startOf(after, "day"))
									: null,
								overall_checkin_after_local: params.overall_checkin_after
									? formatDate(after, "YYYY-MM-DD")
									: null,
								overall_checkin_before: params.overall_checkin_before
									? dateToUTCString(endOf(before, "day"))
									: null,
								overall_checkin_before_local: params.overall_checkin_before
									? formatDate(before, "YYYY-MM-DD")
									: null,
								overall_checkout_after: params.overall_checkout_after
									? dateToUTCString(startOf(after, "day"))
									: null,
								overall_checkout_after_local: params.overall_checkout_after
									? formatDate(after, "YYYY-MM-DD")
									: null,
								overall_checkout_before: params.overall_checkout_before
									? dateToUTCString(endOf(before, "day"))
									: null,
								overall_checkout_before_local: params.overall_checkout_before
									? formatDate(before, "YYYY-MM-DD")
									: null,
							})}
						>
							{({ open }) => {
								return (
									<Button
										level="secondary"
										onClick={() => {
											open("presets")
										}}
									>
										<Icons.DocumentDownload />
									</Button>
								)
							}}
						</DownloadManager>
					</Inline>
				)}
			>
				{({ searchParams, setSearchParams, setSearchParamValue }) => (
					<Tabs layout="col">
						<TabsList>
							<TabItem
								active={
									searchParams.report_type === "overall_checkin"
										? searchParams.report_stage === "upcoming" &&
											searchParams.report_type === "overall_checkin"
										: searchParams.report_stage === "upcoming" &&
											searchParams.report_type === "overall_checkout"
								}
								onClick={() => {
									const {
										display_after,
										display_before,
										check_in_after,
										check_in_before,
										check_out_after,
										check_out_before,
									} = getHotelCheckInOutBeforeAfterDates({
										report_type: searchParams.report_type,
										interval_type: searchParams.interval_type,
										report_stage: "upcoming",
										date:
											searchParams.report_type === "overall_checkin"
												? searchParams.overall_checkin_after
												: searchParams.overall_checkout_after,
									})
									setSearchParams({
										...searchParams,
										page: 1,
										report_type: searchParams.report_type,
										report_stage: "upcoming",
										overall_checkin_after: check_in_after,
										overall_checkin_before: check_in_before,
										overall_checkout_after: check_out_after,
										overall_checkout_before: check_out_before,
										display_after,
										display_before,
									})
								}}
							>
								Upcoming
							</TabItem>
							<TabItem
								active={
									searchParams.report_type === "overall_checkin"
										? searchParams.report_stage === "completed" &&
											searchParams.report_type === "overall_checkin"
										: searchParams.report_stage === "completed" &&
											searchParams.report_type === "overall_checkout"
								}
								onClick={() => {
									const {
										display_after,
										display_before,
										check_in_after,
										check_in_before,
										check_out_after,
										check_out_before,
									} = getHotelCheckInOutBeforeAfterDates({
										report_type: searchParams.report_type,
										interval_type: searchParams.interval_type,
										report_stage: "completed",
										date:
											searchParams.report_type === "overall_checkin"
												? searchParams.overall_checkin_after
												: searchParams.overall_checkout_after,
									})
									setSearchParams({
										...searchParams,
										page: 1,
										report_type: searchParams.report_type,
										report_stage: "completed",
										overall_checkin_after: check_in_after,
										overall_checkin_before: check_in_before,
										overall_checkout_after: check_out_after,
										overall_checkout_before: check_out_before,
										display_after,
										display_before,
									})
								}}
							>
								Completed
							</TabItem>

							<TabItem
								active={
									searchParams.report_type === "overall_checkin"
										? searchParams.report_stage === "all" &&
											searchParams.report_type === "overall_checkin"
										: searchParams.report_stage === "all" &&
											searchParams.report_type === "overall_checkout"
								}
								onClick={() => {
									const {
										check_in_after,
										check_in_before,
										check_out_after,
										check_out_before,
										display_after,
										display_before,
									} = getHotelCheckInOutBeforeAfterDates({
										report_type: searchParams.report_type,
										interval_type: searchParams.interval_type,
										report_stage: "all",
										date:
											searchParams.report_type === "overall_checkin"
												? searchParams.overall_checkin_after
												: searchParams.overall_checkout_after,
									})
									setSearchParams({
										...searchParams,
										page: 1,
										report_type: searchParams.report_type,
										report_stage: "all",
										overall_checkin_after: check_in_after,
										overall_checkin_before: check_in_before,
										overall_checkout_after: check_out_after,
										overall_checkout_before: check_out_before,
										display_after,
										display_before,
									})
								}}
							>
								All
							</TabItem>
						</TabsList>
						<TabContent>
							{!hasAdvancedFilters ? (
								<Component
									initialState={{
										includeChanged: false,
										includeDropped: false,
									}}
									render={({ state, setState }) => (
										<QuickFilterBox
											state={state}
											setState={setState}
											searchParams={searchParams}
											setSearchParams={setSearchParams}
										/>
									)}
								/>
							) : null}
							<ListView<IBooking & { trip: ITrip }, TFilters>
								pageKey="hotel-check-in-outs"
								params={params}
								onPageChange={(page) => setParams({ ...params, page })}
								fetch={(xhr, params) => {
									return XHR(xhr).getBookings(filtersToRequestParams(params))
								}}
							>
								{({ items, refresh }) => {
									return (
										<Table
											hover
											responsive
											headers={[
												"Check In-Out",
												"Hotels",
												"Trip Info",
												"Room/Services",
												"Status",
												"Tag/Comments",
												"Price",
											]}
											bordered
											alignCols={{
												6: "right",
											}}
											rows={items.map((booking, idx) => {
												const {
													id,
													hotel,
													can_view_prices,
													booked_price,
													payments_amount,
													refunding_payments_amount,
													cancellation_charges,
													paid_payments_amount,
													paid_refunding_payments_amount,
													currency,
													room_nights,
													no_of_nights,
													trip,
													extras,
													checkin,
													overall_checkin,
													overall_checkin_local,
													overall_checkout,
													overall_checkout_local,
													details,
													is_shared,
													booking_stage,
													booked,
													voucher,
													can_modify,
													booked_at,
													booking_confirmation_id,
													hotel_contact,
												} = booking

												const trip_start_date = localOrUtcTimestampToLocalDate(
													trip.start_date_local,
													trip.start_date
												)
												const trip_end_date = localOrUtcTimestampToLocalDate(
													trip.end_date_local,
													trip.end_date
												)
												const stay_nights = [
													getDiff(checkin, trip_start_date, "days") + 1,
												]
												for (let i = 1; i < no_of_nights; i++) {
													stay_nights.push(stay_nights[i - 1] + 1)
												}
												const can_Edit = Boolean(can_modify && !trip.is_locked)

												return [
													<Box>
														<Inline gap="1">
															<Time
																timestamp={overall_checkin}
																localTimestamp={overall_checkin_local}
																format="D MMM"
																whiteSpace="nowrap"
															/>
															-
															<Time
																timestamp={overall_checkout}
																localTimestamp={overall_checkout_local}
																whiteSpace="nowrap"
																format="D MMM"
															/>
															<Box>({(stay_nights || []).length}N)</Box>
														</Inline>
													</Box>,
													hotel ? (
														<Box
															display="flex"
															flexDirection={"col"}
															justifyContent={"center"}
															style={{
																minWidth: "100px",
															}}
														>
															<HotelBookingItemInDialog
																hotelBookingId={booking.id}
																readOnly={!can_Edit}
															>
																{({ showDetails }) => (
																	<Text<"a">
																		as="a"
																		href="#hotel-booking-details"
																		fontWeight="semibold"
																		color="accent"
																		onClick={(e) => {
																			e.preventDefault()
																			showDetails()
																		}}
																		title={hotel.name}
																	>
																		{hotel.name.slice(0, 56) +
																			(hotel.name.length > 56 ? "..." : "")}
																	</Text>
																)}
															</HotelBookingItemInDialog>
															<Box fontSize={"sm"}>
																{joinAttributes(
																	hotel.location.short_name,
																	hotel.stars ? (
																		<Stars stars={hotel.stars} showSingleStar />
																	) : null
																)}
															</Box>
														</Box>
													) : (
														"N/A"
													),
													<Box>
														<Link
															to={`/trips/${trip.id}/services-bookings/hotels`}
															anchored
															color="accent"
														>
															{trip.tourist?.name || ""}{" "}
															<Text as="span">
																{joinAttributes(
																	[trip.no_of_adults, `${trip.no_of_adults}A`],
																	[
																		trip.no_of_children,
																		`${trip.no_of_children}C`,
																	]
																)}
															</Text>
														</Link>

														<Box fontSize={"sm"}>
															{joinAttributes(
																`#${trip.id}`,
																<Box as="span" title={trip.trip_source.name}>
																	{trip.trip_source.short_name}
																</Box>,
																trip.reservations_team?.length ? (
																	<Inline
																		gap="1"
																		inline
																		alignItems={"center"}
																		as="span"
																		title={`Reservations Team Members: ${trip.reservations_team.map((member) => member.name).join(", ")}`}
																	>
																		<Icons.User size={"3"} />{" "}
																		{trip.reservations_team[0].name
																			.split(" ")[0]
																			.slice(0, 10) +
																			(trip.reservations_team[0].name.split(
																				" "
																			)[0].length > 10
																				? "..."
																				: "")}
																		{trip.reservations_team.length > 1 && (
																			<Text
																				as="span"
																				color="muted"
																				fontSize={"xs"}
																			>
																				+{trip.reservations_team.length - 1}
																			</Text>
																		)}
																	</Inline>
																) : null
															)}
														</Box>
													</Box>,
													<Box>
														{(room_nights || []).map(function (
															{ room_type, no_of_rooms, nights },
															index,
															array
														) {
															return (
																<Tooltip
																	key={index}
																	content={
																		nights
																			.map((n) => withOrdinalSuffix(n))
																			.join(", ") +
																		" " +
																		pluralize("Night", nights.length)
																	}
																>
																	<Box
																		key={room_type.id}
																		display="inline"
																		fontSize={"sm"}
																	>
																		{no_of_rooms} {room_type.name}
																		{index < array.length - 1 ? ", " : ""}
																	</Box>
																</Tooltip>
															)
														})}

														{details?.length ? (
															<Box marginTop="1" paddingTop="1" fontSize="xs">
																{(() => {
																	const grouped: Array<
																		{
																			night: number
																			meal_plan: IMealPlan
																		}[]
																	> = []
																	let group: {
																		night: number
																		meal_plan: IMealPlan
																	}[] = []

																	details.forEach((detail, i) => {
																		const { date, date_local, meal_plan } =
																			detail
																		const night =
																			getDiff(
																				localOrUtcTimestampToLocalDate(
																					date_local,
																					date
																				),
																				checkin,
																				"days"
																			) + 1
																		const currentDetail = { night, meal_plan }

																		if (
																			group.length === 0 ||
																			group[group.length - 1].meal_plan.name ===
																				meal_plan.name
																		) {
																			group.push(currentDetail)
																		} else {
																			grouped.push(group)
																			group = [currentDetail]
																		}
																	})

																	if (group.length > 0) grouped.push(group)

																	const allSameMealPlan =
																		grouped.length === 1 &&
																		grouped[0].length === details.length

																	if (allSameMealPlan) {
																		return (
																			<Box>{grouped[0][0].meal_plan.name}</Box>
																		)
																	}
																	return grouped.map((group, idx) => {
																		const mealPlanName = group[0].meal_plan.name
																		const nightList = group
																			.map((g) => `${g.night}N`)
																			.join(", ")
																		return (
																			<Box key={idx}>
																				{nightList} - {mealPlanName}
																			</Box>
																		)
																	})
																})()}
															</Box>
														) : (
															<Box fontSize="xs" color="muted" marginTop="1">
																No Room Services
															</Box>
														)}
														{extras?.length ? (
															<Box marginTop="2">
																<Box fontSize={"sm"} fontWeight="semibold">
																	Extras
																</Box>
																<Box fontSize="xs" marginTop="1">
																	{extras.map((extra, i) => {
																		const { date, service, comments } = extra
																		return (
																			<Box key={`${extra.id}-${id}-${i}`}>
																				{stay_nights.length > 1 && date ? (
																					<Box display="inline">
																						{getDiff(
																							date,
																							booking.checkin,
																							"days"
																						) + 1}{" "}
																						N{" - "}
																					</Box>
																				) : null}
																				<Box display="inline">
																					{joinAttributes(service.name)}
																				</Box>
																				{!is_shared && comments ? (
																					<blockquote>{comments}</blockquote>
																				) : null}
																			</Box>
																		)
																	})}
																</Box>
															</Box>
														) : null}
													</Box>,
													<Box
														title={utcTimestampToLocalDateTimeString(
															booking.booking_stage.created_at
														)}
														verticalAlign="middle"
													>
														<HotelBookingCurrentStage
															tripDestinations={trip.destinations}
															booking={booking}
															tripStartDate={trip_start_date}
															tripEndDate={trip_end_date}
															onChangeSuccess={() => refresh()}
															minimal
															readOnly={!can_Edit}
														/>

														{booking_stage.created_by &&
														booking_stage.created_at ? (
															<Box fontSize="sm" color="muted" marginTop="1">
																{joinAttributes(
																	`by ${booking_stage.created_by.name}`,
																	<RelativeTime
																		value={utcTimestampToLocalDate(
																			booking_stage.created_at
																		)}
																	/>
																)}
															</Box>
														) : null}
														{booked && booking_confirmation_id ? (
															<Box
																title="Booking Confirmation ID"
																fontSize="sm"
																marginTop="2"
															>
																CNF: <b>{booking_confirmation_id}</b>
															</Box>
														) : null}
														{booked && hotel_contact ? (
															<Box fontSize="sm" marginTop="1">
																<Text
																	as="span"
																	title="Booking Confirmation By Contact"
																>
																	<Icons.ArrowLeft rotate="180" size="3" />{" "}
																	{hotel_contact.name}{" "}
																</Text>

																<PhoneNumber
																	value={hotel_contact.phone_number}
																	iconOnly
																/>
															</Box>
														) : null}
														{booked ? (
															<>
																{!voucher ? (
																	<>
																		{can_Edit ? (
																			<GenerateVoucher
																				hotel={hotel}
																				booking={booking}
																				isBooked={!!booked_at}
																				bookingConfirmationId={
																					booking_confirmation_id
																				}
																				onGenerate={() => refresh()}
																				minimal
																			>
																				<Box
																					as="span"
																					color="warning"
																					fontSize={"sm"}
																				>
																					<Icons.Document /> Generate
																				</Box>
																			</GenerateVoucher>
																		) : null}
																	</>
																) : (
																	<Inline marginTop="2" alignItems="center">
																		{joinAttributes(
																			<GeneratedVoucherAsset
																				id={voucher.id}
																				fileUrl={voucher.file_url}
																				isGenerating={voucher.is_generating}
																				onChange={() => refresh()}
																				fontSize="sm"
																			/>,
																			!can_Edit ? null : voucher.sent_at ? (
																				<Box
																					title={`Voucher sent on ${utcTimestampToLocalDateTimeString(
																						voucher.sent_at
																					)} by ${voucher.sent_by?.name}`}
																					display="inline"
																					fontSize="sm"
																				>
																					<Icons.Ok color="success" />
																					Sent
																				</Box>
																			) : (
																				<MarkVoucherAsSent
																					voucherId={voucher.id}
																					onSuccess={() => refresh()}
																					minimal
																				>
																					<Inline
																						gap="1"
																						as="span"
																						color="warning"
																						fontSize={"sm"}
																					>
																						<Icons.AttentionSolid />
																						Pending
																					</Inline>
																				</MarkVoucherAsSent>
																			),
																			can_Edit ? (
																				<GenerateVoucher
																					hotel={hotel}
																					booking={booking}
																					isBooked={!!booked_at}
																					bookingConfirmationId={
																						booking_confirmation_id
																					}
																					onGenerate={() => refresh()}
																					isShared={is_shared}
																					minimal
																				>
																					<Icons.Refresh opacity="50" />
																				</GenerateVoucher>
																			) : null
																		)}
																	</Inline>
																)}
															</>
														) : null}
													</Box>,
													<Box verticalAlign="top" fontSize={"sm"}>
														<Inline
															alignItems={"center"}
															gap={booking.booking_stage.tag ? "2" : "0"}
														>
															<Box>{booking.booking_stage.tag || null}</Box>
															{can_Edit ? (
																<UpdateBookingTagInDialog
																	booking={booking}
																	onSuccess={() => refresh()}
																>
																	{({ onEdit }) => (
																		<Button
																			level="tertiary"
																			size="sm"
																			inline={Boolean(
																				booking.booking_stage.tag
																			)}
																			onClick={() => onEdit()}
																		>
																			{booking.booking_stage.tag ? (
																				<Icons.Pencil />
																			) : (
																				<>
																					<Icons.Plus /> Tag
																				</>
																			)}
																		</Button>
																	)}
																</UpdateBookingTagInDialog>
															) : null}
														</Inline>
														{booking.booking_stage.comments ? (
															<Box
																fontSize="sm"
																style={{ minWidth: "80px" }}
																verticalAlign="top"
																color="muted"
																marginTop="1"
															>
																<Box title={booking.booking_stage.comments}>
																	{booking.booking_stage.comments.slice(0, 50) +
																		(booking.booking_stage.comments.length > 50
																			? "..."
																			: "")}
																</Box>
															</Box>
														) : null}
													</Box>,
													can_view_prices ? (
														<Box
															verticalAlign="top"
															style={{ textAlign: "right" }}
														>
															{isNumeric(booked_price) ? (
																<Inline
																	fontWeight="semibold"
																	alignItems="center"
																	justifyContent="end"
																	gap="1"
																>
																	{refunding_payments_amount === null ? (
																		// If not in refund mode, show Green Check when
																		// - Booking is in Booked/Changed stage
																		// - or, when booking with no paid instalments is dropped without cancellation charges
																		// AND, when all payments are paid.
																		// In case payments_amount is null, paid_payments_amount must also be null.
																		[
																			"changed",
																			"booked",
																			"dropped",
																			"trip_dropped",
																		].includes(booking.booking_stage.state) &&
																		paid_payments_amount === payments_amount ? (
																			<Box display="inlineBlock" marginLeft="1">
																				<Icons.OkCircleSolid color="success" />
																			</Box>
																		) : null
																	) : paid_refunding_payments_amount ===
																	  refunding_payments_amount ? (
																		// If in refund mode, show Green Check when
																		// - Refund is complete
																		<Box display="inlineBlock" marginLeft="1">
																			<Icons.OkCircleSolid color="success" />
																		</Box>
																	) : null}
																	<Box>Booking:</Box>
																	<Money
																		money={moneyParseByDecimal(
																			booked_price,
																			currency
																		)}
																		showCurrency
																	/>
																</Inline>
															) : null}

															{isNumeric(payments_amount) ? (
																<Box
																	fontWeight="semibold"
																	fontSize="sm"
																	color="muted"
																>
																	{isNumeric(refunding_payments_amount) &&
																	isNumeric(cancellation_charges) ? (
																		<Box title="Net Payable">
																			Net Payable:{" "}
																			<Money
																				amount={cancellation_charges}
																				currency={currency}
																				showCurrency
																			/>
																		</Box>
																	) : isNumeric(booked_price) &&
																	  Number(payments_amount || 0) ===
																			Number(booked_price || 0) ? null : (
																		<Box title="Net Payable">
																			Net Payable:{" "}
																			<Money
																				amount={payments_amount}
																				currency={currency}
																				showCurrency
																			/>
																		</Box>
																	)}
																</Box>
															) : //show net payaable as 0 if booking_status is either changed or trip dropped
															booking.booking_stage.state === "changed" ||
															  booking.booking_stage.state === "dropped" ||
															  booking.booking_stage.state ===
																	"trip_dropped" ? (
																<Box
																	fontWeight="semibold"
																	fontSize="sm"
																	color="muted"
																	whiteSpace="nowrap"
																	title="Net Payable"
																>
																	Net Payable:{" "}
																	<Money
																		amount={0}
																		currency={currency}
																		showCurrency
																	/>
																</Box>
															) : null}
															{isNumeric(paid_payments_amount) ? (
																<>
																	<Box
																		fontWeight="semibold"
																		fontSize="sm"
																		color="success"
																	>
																		Amount Paid:{" "}
																		<Money
																			amount={paid_payments_amount}
																			currency={currency}
																			showCurrency
																		/>
																	</Box>
																	{isNumeric(payments_amount) ? (
																		Number(payments_amount || 0) >
																		Number(paid_payments_amount || 0) ? (
																			<Box
																				fontWeight="semibold"
																				fontSize="sm"
																				color="muted"
																			>
																				Due Payable:{" "}
																				<Money
																					amount={
																						payments_amount -
																						paid_payments_amount
																					}
																					currency={currency}
																					showCurrency
																				/>
																			</Box>
																		) : null
																	) : null}
																</>
															) : //show paid=0 when "paid_payments_amount" is null && "payments_amount" is not null & "payments_amount" is greater than 0
															isNumeric(payments_amount) &&
															  Number(payments_amount || 0) > 0 ? (
																<Box
																	fontWeight="semibold"
																	fontSize="sm"
																	color="muted"
																	title="Paid"
																>
																	Amount Paid:{" "}
																	<Money
																		amount={0}
																		currency={currency}
																		showCurrency
																	/>
																</Box>
															) : null}
															{isNumeric(refunding_payments_amount) ? (
																<Box
																	fontWeight="semibold"
																	fontSize="sm"
																	color="warning"
																	title="Refund"
																>
																	Refund:{" "}
																	<Money
																		money={moneyParseByDecimal(
																			Number(
																				paid_refunding_payments_amount || 0
																			),
																			currency
																		)}
																		showCurrency
																	/>
																	{" / "}
																	<Money
																		amount={refunding_payments_amount}
																		currency={currency}
																		showCurrency
																	/>
																</Box>
															) : null}
														</Box>
													) : null,
												]
											})}
										/>
									)
								}}
							</ListView>
						</TabContent>
					</Tabs>
				)}
			</Search>
		</Box>
	)
}

export function Filters() {
	const { value: quickbBookingStages } = useFieldValue<
		TFilters["quick_booking_stages"]
	>("quick_booking_stages")
	const { value: reportType } =
		useFieldValue<TFilters["report_type"]>("report_type")
	const { value: bookingStages } =
		useFieldValue<TFilters["booking_stages"]>("booking_stages")
	const { value: paymentStatuses } =
		useFieldValue<TFilters["payment_statuses"]>("payment_statuses")
	const form = useForm()
	const isCheckInReport = reportType === "overall_checkin" || false

	const prevBookingStagesRef = useRef<TFilters["booking_stages"] | undefined>(
		undefined
	)
	const prevPaymentStatusesRef = useRef<
		TFilters["payment_statuses"] | undefined
	>(undefined)

	// Side effect: update quick_booking_stages to default quick stages when booking_stages/payment_statuses gets cleared from advanced filters
	useEffect(() => {
		if (!quickbBookingStages) return

		const prevBookingStages = prevBookingStagesRef.current
		const prevPaymentStatuses = prevPaymentStatusesRef.current

		const bookingJustCleared =
			prevBookingStages &&
			prevBookingStages.length > 0 &&
			(!bookingStages || bookingStages.length === 0)
		const paymentJustCleared =
			prevPaymentStatuses &&
			prevPaymentStatuses.length > 0 &&
			(!paymentStatuses || paymentStatuses.length === 0)

		// Only reset if both are now empty and one of them was just cleared
		const bothEmpty =
			(!bookingStages || bookingStages.length === 0) &&
			(!paymentStatuses || paymentStatuses.length === 0)

		const quickStagesAreDefault =
			JSON.stringify(quickbBookingStages || []) ===
			JSON.stringify(defaultQuickStagesFilter)

		if (
			(bookingJustCleared || paymentJustCleared) &&
			bothEmpty &&
			!quickStagesAreDefault
		) {
			form.change("quick_booking_stages", defaultQuickStagesFilter)
		}

		prevBookingStagesRef.current = bookingStages
		prevPaymentStatusesRef.current = paymentStatuses
	}, [bookingStages, paymentStatuses, form, quickbBookingStages])

	return (
		<Stack gap="4">
			<DatePickerField
				label={isCheckInReport ? "Check-In Date" : "Check-Out Date"}
				name={
					isCheckInReport ? "overall_checkin_after" : "overall_checkout_after"
				}
				onChange={(value) => {
					form.change(
						isCheckInReport
							? "overall_checkin_after"
							: "overall_checkout_after",
						startOf(value || new Date(), "day")
					)

					form.change(
						isCheckInReport
							? "overall_checkin_before"
							: "overall_checkout_before",
						endOf(value || new Date(), "day")
					)

					form.change("interval_type", "day")
					form.change("display_after", value)
					form.change("display_before", value)
					form.change("report_stage", "all")
				}}
				clearable={false}
			/>
			<SelectField
				select={SelectTripDestination}
				name="trip_destinations"
				label="Destinations"
				multiple
				fetchOnMount
			/>
			<SelectField
				label="Trip Sources"
				select={SelectTripSources}
				name="trip_sources"
				multiple
				fetchOnMount
				withRecentlyDisabledWithinDays={30}
			/>
			<SelectField
				select={SelectHotelBookingStages}
				name="booking_stages"
				label="Booking Stages"
				multiple
			/>
			<SelectField
				label="Booking Tags"
				select={SelectHotelBookingStageTags}
				name="booking_tags"
				multiple
				fetchOnMount
			/>
			<SelectField
				label="Payment Status"
				select={SelectHotelBookingPaymentStatuses}
				name="payment_statuses"
				multiple
			/>
			<SelectField
				label="Team"
				select={SelectUsers}
				name="trip_owners"
				multiple
				fetchOnMount
			/>
			<SelectField
				label="Hotels"
				name="hotels"
				select={SelectHotels}
				multiple
			/>
			<SelectField
				label="Hotel Groups"
				name="hotel_groups"
				select={SelectHotelGroup}
				multiple
			/>
		</Stack>
	)
}

function isCurrentTimeWindow(
	date: Date,
	intervalType: TFilters["interval_type"]
) {
	const now = new Date()
	const start = startOf(date, intervalType)
	const end = endOf(date, intervalType)

	return (
		isSameOrAfter(now, start, intervalType) &&
		isSameOrBefore(now, end, intervalType)
	)
}
